var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CloudWatchLogs } from 'aws-sdk';
import { AwsConfig } from '../../../config/aws';
import { baseLogBuilder, initLoggerLogBuilder, } from './LogBuilders';
import UnauthenticatedCredentials from './UnauthenticatedCredentials';
// eslint-disable-next-line import/no-cycle
import NativeModules from '../NativeModules';
const SEQUENCE_TOKEN_ERROR = 'InvalidSequenceTokenException';
const DATA_ALREADY_ACCEPTED_ERROR = 'DataAlreadyAcceptedException';
const RESOURCE_NOT_FOUND_ERROR = 'ResourceNotFoundException';
const logGroupName = AwsConfig.aws_cloudwatch_logs_log_group;
let logStreamName;
let initialized;
let sequenceToken = null;
const stack = [];
export default class MonitoringLogger {
    static areLogsStacked() {
        return stack.length > 0;
    }
    static sequenceToken() {
        return sequenceToken;
    }
    static stack() {
        return stack;
    }
    static isSequenceTokenError(err) {
        return err.code === SEQUENCE_TOKEN_ERROR || err.code === DATA_ALREADY_ACCEPTED_ERROR;
    }
    // TODO: This is not ideal! handle this
    // differently if this gets resolved:
    // https://github.com/aws/aws-sdk-js/issues/2190
    static getTokenFromMessage(errMsg = '') {
        const tokenReg = /: (\d+)$/;
        const nullReg = /: (null)$/;
        const tokenMatch = tokenReg.exec(errMsg);
        const nullMatch = nullReg.exec(errMsg);
        if (tokenMatch && tokenMatch[1]) {
            return tokenMatch[1];
        }
        if (nullMatch && nullMatch[1]) {
            return nullMatch[1];
        }
        return null;
    }
    static getServiceInterface() {
        return __awaiter(this, void 0, void 0, function* () {
            const creds = yield UnauthenticatedCredentials.get();
            return new CloudWatchLogs({
                region: AwsConfig.aws_project_region,
                apiVersion: AwsConfig.aws_cloudwatch_logs_api_version,
                accessKeyId: creds.accessKeyId,
                secretAccessKey: creds.secretAccessKey,
                sessionToken: creds.sessionToken,
            });
        });
    }
    static createLogEvent(log) {
        return {
            message: JSON.stringify(baseLogBuilder(log), null, 4),
            timestamp: Date.now(),
        };
    }
    static putLogEventsParams(logEvents) {
        return {
            logEvents,
            logGroupName,
            logStreamName,
            sequenceToken: sequenceToken === 'null' ? null : sequenceToken,
        };
    }
    static initNewStreamCallback(resolve, reject, err, data) {
        try {
            if (err) {
                reject(`Unable to initialize by creating new stream. response error: ${err}, response data: ${data}`);
            }
            else {
                sequenceToken = null;
                resolve();
            }
        }
        catch (e) {
            reject(e);
        }
    }
    static setLogStreamName() {
        return __awaiter(this, void 0, void 0, function* () {
            if (logStreamName === undefined) {
                const anonymousDeviceId = yield NativeModules.DeviceInfoProvider.getAnonymousDeviceId();
                logStreamName = `${anonymousDeviceId}/${Math.random().toString(36).substring(2, 15)}`;
            }
        });
    }
    static initCallback(resolve, reject, err, data) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (data && data.nextSequenceToken) {
                    sequenceToken = data.nextSequenceToken;
                    resolve();
                }
                else if (err && err.code === RESOURCE_NOT_FOUND_ERROR) {
                    yield MonitoringLogger.setLogStreamName();
                    const cw = yield MonitoringLogger.getServiceInterface();
                    const params = { logGroupName, logStreamName };
                    cw.createLogStream(params, MonitoringLogger.initNewStreamCallback.bind(this, resolve, reject));
                }
                else if (MonitoringLogger.isSequenceTokenError(err)) {
                    const token = MonitoringLogger.getTokenFromMessage(err.message);
                    if (token) {
                        sequenceToken = token;
                        resolve();
                    }
                    else {
                        reject(`Unable to sequence token during initialization: ${err.message}`);
                    }
                }
                else {
                    reject(`Unable to initialize. response error: ${err}, responsedata: ${data}`);
                }
            }
            catch (e) {
                reject(e);
            }
        });
    }
    static init() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (!initialized) {
                    const cwPromise = MonitoringLogger.getServiceInterface();
                    const logStreamNamePromise = MonitoringLogger.setLogStreamName();
                    initialized = Promise.all([cwPromise, logStreamNamePromise])
                        .then(([cw]) => {
                        const initLogEvents = [this.createLogEvent(initLoggerLogBuilder())];
                        const params = MonitoringLogger.putLogEventsParams(initLogEvents);
                        return new Promise((resolve, reject) => {
                            cw.putLogEvents(params, MonitoringLogger.initCallback.bind(this, resolve, reject));
                        }).catch((err) => {
                            initialized = Promise.reject(err);
                            return initialized;
                        });
                    })
                        .catch((error) => {
                        initialized = Promise.reject(error);
                        return initialized;
                    });
                }
            }
            catch (e) {
                initialized = Promise.reject(e);
            }
            return initialized;
        });
    }
    // eslint-disable-next-line consistent-return
    static removeFromStackAndExecuteNext() {
        return __awaiter(this, void 0, void 0, function* () {
            stack.shift();
            if (MonitoringLogger.areLogsStacked()) {
                try {
                    yield stack[0]();
                    return yield MonitoringLogger.removeFromStackAndExecuteNext();
                }
                catch (e) {
                    return MonitoringLogger.removeFromStackAndExecuteNext();
                }
            }
        });
    }
    static put(logs) {
        return __awaiter(this, void 0, void 0, function* () {
            yield MonitoringLogger.init();
            const cw = yield MonitoringLogger.getServiceInterface();
            const params = MonitoringLogger.putLogEventsParams(logs);
            return new Promise((resolve, reject) => {
                cw.putLogEvents(params, (err, data) => {
                    try {
                        if (!err && data && data.nextSequenceToken) {
                            sequenceToken = data.nextSequenceToken;
                        }
                        else if (MonitoringLogger.isSequenceTokenError(err)) {
                            const token = MonitoringLogger.getTokenFromMessage(err.message);
                            if (token) {
                                sequenceToken = token;
                            }
                        }
                        resolve();
                    }
                    catch (e) {
                        reject();
                    }
                });
            });
        });
    }
    static addToStackAndWrite(logEvents) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const writeLog = MonitoringLogger.put.bind(this, logEvents);
                const wasEmpty = !MonitoringLogger.areLogsStacked();
                stack.push(writeLog);
                if (wasEmpty) {
                    yield writeLog();
                    MonitoringLogger.removeFromStackAndExecuteNext();
                }
            }
            catch (err) {
                MonitoringLogger.removeFromStackAndExecuteNext();
            }
        });
    }
    static write(logBuilder, logObject) {
        if (!logBuilder || !logObject)
            return;
        const logEvent = this.createLogEvent(logBuilder(logObject));
        MonitoringLogger.addToStackAndWrite([logEvent]);
    }
}
//# sourceMappingURL=MonitoringLogger.js.map